// Tap an exercise (on Today) to view its layout and tweak the rep scheme, rest,
// sets, and weight jump. Saves straight to the program exercise.

import { updateProgramExercise, loadLiftNotes } from '../lib/grit-data.js';
import { openNotes } from './notes-sheet.js';
import { notesByLift } from '../lib/lift-notes.js';
import { localISO } from '../lib/phase.js';
import { setName, resetName, displayName, escapeHtml } from '../lib/exercise-names.js';
import { toast } from './toast.js';

let overlay = null;
const REST_OPTS = [60, 90, 120, 180, 240]; // book: ~2 min accessory, ~3 min big lifts, up to 4 (p.252, 362)
const fmtRest = (s) => `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;

export function openExerciseEdit(pe, onSaved) {
  closeNow();
  const tracking = pe.tracking || 'weight';
  const isTime = tracking === 'time';
  const isWeight = tracking === 'weight';
  const unit = isTime ? 'sec' : 'reps';
  const exId = pe.exercise_id || pe.exercise?.id;
  const original = pe.exercise?.name || 'Exercise';
  let curName = displayName(pe.exercise) || original; // resolved (override or original)
  let noteCount = 0;                                   // this lift's notes
  const countNotes = () => loadLiftNotes().then((rows) => { noteCount = (notesByLift(rows)[exId] || []).length; if (overlay) render(); });
  countNotes();

  overlay = document.createElement('div');
  overlay.className = 'fixed inset-0 z-[120] flex items-end justify-center bg-black/30';

  function field(key, label, val) {
    return `<label class="flex flex-col gap-1">
      <input data-f="${key}" type="number" inputmode="numeric" value="${val ?? ''}" class="grit-input text-center py-2.5 font-bold text-[15px]" />
      <span class="text-[12px] text-inksoft text-center">${label}</span>
    </label>`;
  }

  function render() {
    overlay.innerHTML = `
      <div class="grit-sheet">
        <div class="grit-sheet-handle"></div>
        <input id="ex-name" value="${escapeHtml(curName)}" class="grit-input w-full font-bold text-[17px] py-2" />
        <div class="flex items-center justify-between mt-1 mb-4 gap-2">
          <span class="text-[13px] text-inksoft truncate">${pe.exercise?.muscle_group || ''}${isTime ? ' · timed' : (tracking === 'bodyweight' ? ' · bodyweight' : '')}</span>
          ${curName !== original ? '<button id="ex-name-reset" class="text-[13px] text-grit font-bold flex-none">Reset name</button>' : ''}
        </div>

        <div class="grid grid-cols-3 gap-2">
          ${field('target_sets', 'Sets', pe.target_sets)}
          ${field('rep_low', 'Min ' + unit, pe.rep_low)}
          ${field('rep_high', 'Max ' + unit, pe.rep_high)}
        </div>

        <div class="mt-4">
          <div class="text-[13px] font-bold text-inksoft mb-1.5">Rest between sets</div>
          <div class="flex gap-1.5">
            ${REST_OPTS.map((s) => `<button data-rest="${s}" class=\"flex-1 rounded-full min-h-[44px] text-[15px] font-semibold active:scale-95 transition-transform ${Number(pe.rest_seconds) === s ? 'bg-ink text-white' : 'bg-fill text-ink'}\">${fmtRest(s)}</button>`).join('')}
          </div>
        </div>

        ${isWeight ? `<div class="grid grid-cols-2 gap-2 mt-4">
          ${field('increment_lb', 'Weight jump lb', pe.increment_lb)}
          ${field('start_weight_lb', 'Start lb', pe.start_weight_lb)}
        </div>` : ''}

        <button id="ex-notes" class="w-full flex items-center justify-between min-h-[48px] mt-4 text-[15px] font-semibold text-ink">
          <span>Notes${noteCount ? ` (${noteCount})` : ''}</span><span class="text-grit-press">${noteCount ? 'See all' : 'Add one'}</span>
        </button>

        <button data-done class="grit-btn mt-5">Done</button>
      </div>`;

    overlay.querySelectorAll('input[data-f]').forEach((inp) => {
      inp.onchange = () => save(inp.dataset.f, inp.value === '' ? null : Number(inp.value), false);
    });
    overlay.querySelectorAll('[data-rest]').forEach((b) => {
      b.onclick = () => save('rest_seconds', Number(b.dataset.rest), true);
    });

    const nameInp = overlay.querySelector('#ex-name');
    if (nameInp) nameInp.onchange = async () => {
      const ok = await setName(exId, nameInp.value);
      if (!ok) { toast("Couldn't rename, try again", { tone: 'bad' }); return; }
      curName = displayName(pe.exercise) || original;
      toast('Renamed'); render();
    };
    const resetBtn = overlay.querySelector('#ex-name-reset');
    if (resetBtn) resetBtn.onclick = async () => {
      const ok = await resetName(exId);
      if (!ok) { toast("Couldn't reset, try again", { tone: 'bad' }); return; }
      curName = original; toast('Back to original'); render();
    };
    overlay.querySelector('#ex-notes').onclick = () => openNotes({ title: curName, lifts: [{ exercise_id: exId, exercise: pe.exercise }], date: localISO(), onChange: countNotes });

    overlay.querySelector('[data-done]').onclick = () => close(onSaved);
  }

  const REQUIRED = ['target_sets', 'rep_low', 'rep_high', 'rest_seconds'];
  async function save(fieldKey, val, repaint) {
    if (REQUIRED.includes(fieldKey)) {
      if (val == null || Number.isNaN(val) || val <= 0) { render(); return; } // reject blank/zero, restore prior
      if (fieldKey === 'target_sets') val = Math.max(1, Math.round(val));
    }
    const ok = await updateProgramExercise(pe.id, { [fieldKey]: val });
    if (!ok) { toast("Didn't save, try again", { tone: 'bad' }); render(); return; } // restore on failure
    pe[fieldKey] = val; // only trust local state once the DB took it
    toast('Saved');
    if (repaint) render();
  }

  overlay.addEventListener('click', (e) => { if (e.target === overlay) close(onSaved); });
  render();
  document.body.appendChild(overlay);
}

function close(onSaved) { closeNow(); if (onSaved) onSaved(); }
function closeNow() { if (overlay) { overlay.remove(); overlay = null; } }
